import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ProductService {

  private api = 'https://superbangladesh-api-1.onrender.com/api/products';

  constructor(
    private http: HttpClient
  ) {}

  // ==========================
  // ALL PRODUCTS
  // ==========================
  getProducts(): Observable<any[]> {
    return this.http.get<any[]>(this.api);
  }

  // ==========================
  // ADMIN — পেজ ধরে প্রোডাক্ট (৪৬ হাজার+ প্রোডাক্ট একসাথে না এনে)
  // ==========================
  getAdminPage(page: number, size: number, q: string = ''): Observable<any> {
    let url = `${this.api}/admin-page?page=${page}&size=${size}`;
    if (q && q.trim()) {
      url += `&q=${encodeURIComponent(q.trim())}`;
    }
    return this.http.get<any>(url);
  }

  // ==========================
  // ALL PRODUCTS (cached — হোম ও অফার পেজ একই ডাটা ব্যবহার করে)
  // ৫ মিনিট পর্যন্ত আগের রেসপন্সই আবার দেয়, তাই অফার পেজ সাথে সাথে খোলে।
  // admin প্যানেল এটা ব্যবহার করে না — সেখানে সবসময় সর্বশেষ ডাটা (getProducts)।
  // ==========================
  private allCache$?: Observable<any[]>;

  private allCacheAt = 0;

  getProductsCached(): Observable<any[]> {

    const now = Date.now();

    if (!this.allCache$ || now - this.allCacheAt > 300000) {

      this.allCacheAt = now;

      this.allCache$ = this.http.get<any[]>(this.api).pipe(

        catchError(err => {
          this.allCache$ = undefined;
          return throwError(() => err);
        }),

        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.allCache$;
  }

  // ==========================
  // OFFERS (শুধু ডিসকাউন্টের প্রোডাক্ট — ব্যাকএন্ডের /offers API)
  // পুরো ক্যাটালগ না এনে শুধু ছাড়ের প্রোডাক্ট আনে, তাই অফার পেজ অনেক দ্রুত।
  // ব্যাকএন্ড এখনো আপডেট না হলে আগের পদ্ধতিতে (সব প্রোডাক্ট থেকে ফিল্টার) চলবে।
  // ==========================
  private offersCache$?: Observable<any[]>;

  private offersCacheAt = 0;

  // সর্বশেষ পাওয়া অফার লিস্ট (মেমরি + localStorage) — পেজ খোলার সাথে সাথে
  // এটা দিয়ে দেখানো যায়, লোডিং/ঘোরা লাগে না; পেছনে চুপচাপ নতুন ডাটা আসে।
  private offersSnap: any[] | null = null;

  private readonly OFFERS_KEY = 'sb_offers_v1';

  peekOffers(): any[] | null {

    if (this.offersSnap) {
      return this.offersSnap;
    }

    try {

      if (typeof localStorage === 'undefined') {
        return null;
      }

      const raw = localStorage.getItem(this.OFFERS_KEY);

      if (!raw) {
        return null;
      }

      const o = JSON.parse(raw);

      if (!o || !Array.isArray(o.list) || Date.now() - o.at > 30 * 60000) {
        return null;
      }

      this.offersSnap = o.list;

      return this.offersSnap;

    } catch {
      return null;
    }
  }

  private saveOffers(list: any[]): void {

    this.offersSnap = list;

    try {

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(
          this.OFFERS_KEY,
          JSON.stringify({ at: Date.now(), list })
        );
      }

    } catch {
      // জায়গা না থাকলে শুধু মেমরিতেই থাকবে
    }
  }

  getOffersCached(): Observable<any[]> {

    const now = Date.now();

    if (!this.offersCache$ || now - this.offersCacheAt > 300000) {

      this.offersCacheAt = now;

      this.offersCache$ = this.http.get<any[]>(`${this.api}/offers`).pipe(

        catchError(() =>
          this.getProductsCached().pipe(
            map(list =>
              (Array.isArray(list) ? list : [])
                .filter(p => Number(p?.discount) > 0)
            )
          )
        ),

        catchError(err => {
          this.offersCache$ = undefined;
          return throwError(() => err);
        }),

        tap(list => this.saveOffers(Array.isArray(list) ? list : [])),

        shareReplay({ bufferSize: 1, refCount: false })
      );
    }

    return this.offersCache$;
  }

  // ==========================
  // PRODUCT DETAILS
  // ==========================
  getProductById(id: number): Observable<any> {
    return this.http.get<any>(`${this.api}/${id}`);
  }

  // ==========================
  // CATEGORY PRODUCTS
  // ==========================
  getByCategory(id: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.api}/category/${id}`);
  }

  // ==========================
  // SEARCH PRODUCTS
  // ==========================
  searchProducts(
    keyword: string
  ): Observable<any[]> {

    return this.http.get<any[]>(
      `${this.api}/search?keyword=${encodeURIComponent(keyword)}`
    );
  }

  // ==========================
  // ADD PRODUCT
  // ==========================
  addProduct(
    data: any
  ): Observable<any> {

    return this.http.post<any>(
      this.api,
      data
    );
  }

  // ==========================
  // UPDATE PRODUCT
  // ==========================
  updateProduct(
    id: number,
    data: any
  ): Observable<any> {

    return this.http.put<any>(
      `${this.api}/${id}`,
      data
    );
  }

  // ==========================
  // DELETE PRODUCT
  // ==========================
  deleteProduct(
    id: number
  ): Observable<any> {

    return this.http.delete<any>(
      `${this.api}/${id}`
    );
  }

  // ==========================
  // 🗑️ PRODUCT TRASH (delete করলে এখন এখানে যায়, recoverable)
  // ==========================
  getTrash(): Observable<any> {
    return this.http.get<any>(`${this.api}/admin-trash`);
  }

  restoreProduct(id: number): Observable<any> {
    return this.http.post<any>(`${this.api}/${id}/restore`, {});
  }

  permanentlyDeleteProduct(id: number): Observable<any> {
    return this.http.delete<any>(`${this.api}/${id}/permanent`);
  }

  // ==========================
  // IMAGE UPLOAD
  // ==========================
  uploadImage(
    file: File,
    folder: string
  ): Observable<string> {

    const formData = new FormData();

    formData.append(
      'file',
      file
    );

    formData.append(
      'folder',
      folder
    );

    return this.http.post(
      `${this.api}/upload`,
      formData,
      {
        responseType: 'text'
      }
    );
  }

}